import Utils from './utils';
import { Int64S } from './int64s';

class BufferReader {
  protected readPosition: number = 0;
  protected viewOffset: number;
  protected viewLength: number;
  protected view: DataView;

  constructor(buffer: ArrayBuffer, offset: number, length: number) {
    this.viewOffset = offset;
    this.viewLength = length;
    this.view = new DataView(buffer, offset, length);
  }

  public read(length: number): BufferReader {
    const data = new BufferReader(this.view.buffer, this.readPosition + this.viewOffset, length);
    this.readPosition += length;
    return data;
  }

  readBoolean(): boolean {
    const value = this.view.getUint8(this.readPosition) !== 0;
    this.readPosition += 1;
    return value;
  }

  // readUnsignedShort
  readUint16(): number {
    const value = this.view.getUint16(this.readPosition);
    this.readPosition += 2;
    return value;
  }

  // readShort
  readInt16(): number {
    const value = this.view.getInt16(this.readPosition);
    this.readPosition += 2;
    return value;
  }

  // readInt
  readInt32(): number {
    const value = this.view.getInt32(this.readPosition);
    this.readPosition += 4;
    return value;
  }

  // readLong as String
  readInt64S(): string {
    const arr = this.readUint8Array(8);
    return Int64S(arr);
  }

  // readUnsignedByte
  readUint8(): number {
    const value = this.view.getUint8(this.readPosition);
    this.readPosition += 1;
    return value;
  }

  // readByte
  readInt8(): number {
    const value = this.view.getInt8(this.readPosition);
    this.readPosition += 1;
    return value;
  }

  // read Float32
  readFloat32(): number {
    const value = this.view.getFloat32(this.readPosition);
    this.readPosition += 4;
    return value;
  }

  readUTF(length: number): string {
    if (length < 0) {
      // console.warn('attempt to read outside of bounds - negative length');
      length = 0;
    } else if (length + this.readPosition + this.viewOffset > this.view.buffer.byteLength) {
      // console.warn('attempt to read outside of bounds');
      length = this.view.buffer.byteLength - (this.readPosition + this.viewOffset);
    }
    const array = new Uint8Array(this.view.buffer, this.readPosition + this.viewOffset, length);
    const str = Utils.arrayToUTF(array);
    this.readPosition += length;
    return str;
  }

  readUint8Array(length: number): Uint8Array {
    const value = new Uint8Array(this.view.buffer, this.viewOffset + this.readPosition, length);
    this.readPosition += length;
    return value;
  }

  toUint8Array(onlyUnread?: boolean): Uint8Array {
    let offset = this.viewOffset;
    if (onlyUnread) {
      offset += this.readPosition;
    }
    return new Uint8Array(this.view.buffer, offset, this.viewLength);
  }

  readUTFWithLength(): string {
    const length = this.readInt16();
    return this.readUTF(length);
  }
}

export default BufferReader;
